import {otsBaseApi} from "../../ots-api";
import {Transport, TransportPayload} from "../../../model/transport";
import {removeUndefined} from "../../../util/util";
import {driverActivityCacheUpdate, mergeTransportIntoDriverActivity, upsertTransportIntoDriverActivity} from "../driver-activity/driver-activity-store";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Transports", "Ttas", "DriverActivity"]});

export const transportApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTransport: builder.query<Transport, {
      id: string,
      database?: string | undefined
    }>({
      query: ({id, database}) => ({
        url: `/transport/${id}`,
        method: "GET",
        params: removeUndefined({
          serviceLookup: database
        })
      }),
      providesTags: (result, error, {id}) => [{type: "Transports", id}]
    }),
    createTransport: builder.mutation<Transport, TransportPayload>({
      query: (mapping) => ({
        url: "/transport",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [
        {type: "Transports", id: "LIST"},
        {type: "Ttas", id: "LIST"},
        {type: "DriverActivity", id: "LIST"},
      ],
      // A new pickup or delivery joins the days it was made for, without them being fetched again.
      async onQueryStarted(_arg, {dispatch, queryFulfilled, getState}) {
        try {
          const {data} = await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft, window) => upsertTransportIntoDriverActivity(draft, data, window))
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    updateTransport: builder.mutation<Transport, TransportPayload>({
      query: (mapping) => ({
        url: "/transport",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Transports", id},
        {type: "Ttas", id: "LIST"},
      ],
      async onQueryStarted(_arg, {dispatch, queryFulfilled, getState}) {
        try {
          const { data } = await queryFulfilled
          // Patch the cached days in place instead of refetching them; a day of driver activity is slow to load.
          driverActivityCacheUpdate(dispatch, getState(), (draft) => mergeTransportIntoDriverActivity(draft, data))
        } catch (error) {
          console.debug(error)
        }
      },
    }),
    deleteTransport: builder.mutation<void, Partial<Transport>>({
      query: (mapping) => ({
        url: "/transport",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Transports", id},
        {type: "Ttas", id: "LIST"}
      ],
      async onQueryStarted({id}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            const index = draft.findIndex(s => s.pickupTransport?.id === id || s.deliveryTransport?.id === id)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    })
  }),
  overrideExisting: false
});

export const {
  useGetTransportQuery,
  useCreateTransportMutation,
  useUpdateTransportMutation,
  useDeleteTransportMutation,
} = transportApi;
